/**
 * PermissionDropdown — self-contained permission selector with portal menu;
 * owns its own open/pos state + outside-click effect.
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export function PermissionDropdown({ value, onChange, onRemove, disabled }: {
  value: string;
  onChange: (v: 'read' | 'read_write' | 'admin') => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  const label = value === 'admin' ? 'Admin' : value === 'read_write' ? 'Read & Write' : 'Read Only';

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!btnRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setPos({ top: rect.bottom + 4, left: rect.right - 160, width: 160 });
          }
          setOpen(!open);
        }}
        style={{
          padding: '4px 10px', fontSize: 14, borderRadius: 6,
          border: '1px solid var(--color-border, #DADCE0)',
          background: 'var(--color-surface, #fff)',
          color: 'var(--color-text-primary, #1a1c1e)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
          opacity: disabled ? 0.4 : 1,
        }}
      >
        <span>{label}</span>
        <svg width="10" height="10" viewBox="0 0 20 20" fill="none" style={{ opacity: 0.5, flexShrink: 0 }}>
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 8l4 4 4-4" />
        </svg>
      </button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
            background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #DADCE0)',
            borderRadius: 8, boxShadow: 'var(--popover-shadow, 0 4px 16px rgba(0,0,0,0.12))', zIndex: 20000,
            overflow: 'hidden', display: 'flex', flexDirection: 'column',
          }}
        >
          {[
            { key: 'admin', label: 'Admin' },
            { key: 'read_write', label: 'Read & Write' },
            { key: 'read', label: 'Read Only' },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => {
                if (opt.key !== value) onChange(opt.key as 'read' | 'read_write' | 'admin');
                setOpen(false);
              }}
              style={{
                display: 'block', width: '100%', padding: '8px 12px', border: 'none',
                background: opt.key === value ? 'var(--color-surface-hover, #F1F3F4)' : 'transparent',
                color: 'var(--color-text, #1a1a1a)', fontSize: 13, textAlign: 'left', cursor: 'pointer',
                fontWeight: opt.key === value ? 500 : 400,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-surface-hover, #F1F3F4)'}
              onMouseLeave={(e) => e.currentTarget.style.background = opt.key === value ? 'var(--color-surface-hover, #F1F3F4)' : 'transparent'}
            >
              {opt.label}
            </button>
          ))}
          <div style={{ borderTop: '1px solid var(--color-border-soft, #ECECEF)', margin: '0' }} />
          <button
            type="button"
            onClick={() => { setOpen(false); onRemove(); }}
            style={{
              display: 'block', width: '100%', padding: '8px 12px', border: 'none',
              background: 'transparent', color: '#EA4335', fontSize: 13, textAlign: 'left',
              cursor: 'pointer', fontWeight: 400,
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(234, 67, 53, 0.06)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Remove
          </button>
        </div>,
        document.body
      )}
    </>
  );
}
